import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, FileText, Image, Mail, FileCode, DollarSign, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fileTypeIcons = {
  photo: Image,
  email: Mail,
  drawing: FileCode,
  site_instruction: FileText,
  cost: DollarSign,
  program: Calendar,
  other: FileText,
};

export default function FileUploadManager({ attachments = [], onAttachmentsChange, allowedTypes = ['all'], compact = false }) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);

  const fileTypes = [
    { value: 'photo', label: 'Photos', accept: 'image/*', multiple: true, needsRef: false },
    { value: 'email', label: 'Emails (PDF/.msg)', accept: '.pdf,.msg', multiple: true, needsRef: false },
    { value: 'drawing', label: 'Drawings (PDF/DWG)', accept: '.pdf,.dwg', multiple: false, needsRef: true, refLabel: 'Drawing Number' },
    { value: 'site_instruction', label: 'Site Instructions', accept: '.pdf', multiple: false, needsRef: true, refLabel: 'SI Number' },
    { value: 'cost', label: 'Cost Analysis', accept: '.pdf,.xlsx,.xls', multiple: false, needsRef: false },
    { value: 'program', label: 'Program/Schedule', accept: '.pdf,.mpp,.xer,.xml,.p6', multiple: false, needsRef: false },
    { value: 'other', label: 'Other Documents', accept: '*/*', multiple: true, needsRef: false },
  ];

  const availableTypes = allowedTypes.includes('all') 
    ? fileTypes 
    : fileTypes.filter(ft => allowedTypes.includes(ft.value));

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const currentType = fileTypes.find(ft => ft.value === selectedFileType);
    if (!currentType) return;

    // Check if reference number is needed
    if (currentType.needsRef && !referenceNumber.trim()) {
      toast.error(`Please enter ${currentType.refLabel}`);
      return;
    }

    setUploading(true);

    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', selectedFileType);
        if (currentType.needsRef && referenceNumber) {
          formData.append('reference_number', referenceNumber);
        }

        const token = localStorage.getItem('token');
        const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedFiles.push(response.data);
      }

      onAttachmentsChange([...attachments, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
      setUploadDialogOpen(false);
      setReferenceNumber('');
      setSelectedFileType('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (attachmentId) => {
    onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
  };

  const openUploadDialog = (fileType) => {
    setSelectedFileType(fileType);
    setUploadDialogOpen(true);
  };

  // Drag & Drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const detectFileType = (file) => {
    const name = file.name.toLowerCase();
    const type = file.type;
    
    if (type.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => name.endsWith(ext))) {
      return 'photo';
    }
    if (name.endsWith('.msg') || name.endsWith('.eml')) {
      return 'email';
    }
    if (name.endsWith('.dwg') || (name.includes('drawing') && name.endsWith('.pdf'))) {
      return 'drawing';
    }
    if (name.includes('si-') || name.includes('site instruction') || name.includes('site_instruction')) {
      return 'site_instruction';
    }
    if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.includes('cost')) {
      return 'cost';
    }
    if (name.endsWith('.mpp') || name.endsWith('.xer') || name.endsWith('.p6') || name.includes('program') || name.includes('schedule')) {
      return 'program';
    }
    return 'other';
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const detectedType = detectFileType(file);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', detectedType);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedFiles.push(response.data);
      }

      onAttachmentsChange([...attachments, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [attachments, onAttachmentsChange]);

  return (
    <div className="space-y-4">
      {/* Drag & Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 transition-all ${
          isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-300 bg-slate-50 hover:border-slate-400'
        }`}
      >
        <div className="text-center">
          {isDragging ? (
            <div className="text-blue-600 py-4">
              <Upload className="h-8 w-8 mx-auto mb-2 animate-bounce" />
              <p className="font-medium">Drop files here to upload</p>
            </div>
          ) : (
            <>
              <Upload className="h-6 w-6 mx-auto mb-2 text-slate-400" />
              <p className="text-xs text-slate-500 mb-3">
                <strong>Drag & drop</strong> files here, or click a button below
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {availableTypes.map((type) => {
                  const Icon = fileTypeIcons[type.value];
                  return (
                    <Button
                      key={type.value}
                      type="button"
                      onClick={() => openUploadDialog(type.value)}
                      variant="outline"
                      size="sm"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-sm font-medium uppercase tracking-wide text-xs px-3 py-1.5"
                    >
                      <Icon className="h-3 w-3 mr-1.5" />
                      {compact ? type.label.split(' ')[0] : type.label}
                    </Button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        
        {uploading && (
          <div className="mt-3 text-center">
            <div className="inline-flex items-center gap-2 text-sm text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              Uploading...
            </div>
          </div>
        )}
      </div>

      {/* Uploaded Files List */}
      {attachments.length > 0 && (
        <div className="border border-slate-200 rounded-sm p-3 overflow-hidden">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Uploaded Files</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {attachments.map((attachment) => {
              const Icon = fileTypeIcons[attachment.file_type] || FileText;
              return (
                <div key={attachment.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-sm overflow-hidden">
                  <Icon className="h-4 w-4 text-slate-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="text-sm font-medium text-slate-900 truncate" title={attachment.file_name}>
                      {attachment.file_name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500 truncate">
                      <span className="uppercase flex-shrink-0">{attachment.file_type.replace('_', ' ')}</span>
                      {attachment.reference_number && (
                        <>
                          <span className="flex-shrink-0">•</span>
                          <span className="font-mono truncate">#{attachment.reference_number}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 h-7 w-7 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">
              Upload {fileTypes.find(ft => ft.value === selectedFileType)?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {fileTypes.find(ft => ft.value === selectedFileType)?.needsRef && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  {fileTypes.find(ft => ft.value === selectedFileType)?.refLabel}
                </Label>
                <Input
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder={`Enter ${fileTypes.find(ft => ft.value === selectedFileType)?.refLabel}`}
                  className="rounded-sm"
                />
              </div>
            )}
            
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Select File(s)
              </Label>
              <Input
                type="file"
                accept={fileTypes.find(ft => ft.value === selectedFileType)?.accept}
                multiple={fileTypes.find(ft => ft.value === selectedFileType)?.multiple}
                onChange={handleFileSelect}
                disabled={uploading}
                className="rounded-sm"
              />
            </div>

            {uploading && (
              <p className="text-sm text-slate-600">Uploading...</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
